<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import { useConversationSearch } from 'dashboard/composables/useConversationSearch';

export default {
  name: 'ForwardConversationModal',
  components: {
    Avatar,
    Spinner,
    EsqueletoLista,
    EstadoVazio,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    isForwarding: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'forward'],
  setup() {
    // Busca no SERVIDOR, e de propósito sem filtro de aba nem de status.
    //
    // POR QUE (queixa da Dra. Jullya, 2026-08-12): a lista só oferecia o que
    // já estava carregado na tela de atendimento — ou seja, a aba e o filtro
    // em que a pessoa estava. Quem estivesse em "Minhas" só conseguia
    // encaminhar para as próprias conversas, e digitar o nome de um cliente
    // fora daquela lista não achava nada: parecia que a conversa não existia.
    const assigneeType = ref('all');
    const status = ref('all');
    return { busca: useConversationSearch({ assigneeType, status }) };
  },
  data() {
    return {
      searchQuery: '',
      selectedConversationIds: [],
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      conversations: 'getAllConversations',
      currentConversationId: 'getSelectedChat',
    }),
    filteredConversations() {
      const query = this.searchQuery.toLowerCase().trim();

      // A CONVERSA ATUAL NÃO É MAIS EXCLUÍDA.
      //
      // Havia um `if (conv.id === atual) return false` aqui. Era exatamente o
      // "inclusive o meu próprio" que a Dra. Jullya não conseguia escolher:
      // pegar uma mensagem lá de cima e trazer de volta para o fim da MESMA
      // conversa era impossível. Encaminhar para onde se está é um uso
      // legítimo, não um engano a proteger.
      const daTela = this.conversations.filter(conv => {
        if (!query) return true;

        const senderName = conv.meta?.sender?.name?.toLowerCase() || '';
        const senderPhone = conv.meta?.sender?.phone_number || '';
        const convId = String(conv.id);

        return (
          senderName.includes(query) ||
          senderPhone.includes(query) ||
          convId.includes(query)
        );
      });

      // As da tela primeiro (aparecem no ato, sem esperar a rede), depois as
      // que o servidor achou. Dedup por id: a mesma conversa costuma estar nos
      // dois lados, e listar duas vezes faria a pessoa marcar a mesma caixa
      // duas vezes achando que são conversas diferentes.
      const vistos = new Set(daTela.map(c => c.id));
      const doServidor = (this.busca.results.value || []).filter(
        c => !vistos.has(c.id)
      );

      return [...daTela, ...doServidor].slice(0, 50);
    },
    buscandoNoServidor() {
      return this.busca.isLoading.value;
    },
    erroDaBusca() {
      return this.busca.error.value;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.selectedConversationIds = [];
        this.searchQuery = '';
        // Limpa também o resultado do servidor: sem isto, reabrir o modal
        // mostraria a busca da vez anterior com o campo já vazio.
        this.busca.reset();
      }
    },
    // O composable tem debounce próprio (300ms) — digitar não dispara uma
    // requisição por tecla.
    searchQuery(valor) {
      this.busca.query.value = valor;
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    handleForward() {
      this.$emit('forward', this.selectedConversationIds);
    },
    getLastMessagePreview(conversation) {
      const lastMessage = conversation.messages?.[0];
      if (!lastMessage) return '';

      if (lastMessage.content) {
        const truncated = lastMessage.content.substring(0, 50);
        return lastMessage.content.length > 50 ? `${truncated}...` : truncated;
      }

      if (lastMessage.attachments?.length > 0) {
        const type = lastMessage.attachments[0].file_type;
        return `[${type}]`;
      }

      return '';
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <!-- Casca do produto (`.advup-modal`), topo azul — cada linha é uma
         conversa, e azul é conversa. Era o modal antigo do Chatwoot com os
         botões dele. -->
    <Transition name="advup-saida">
      <div v-if="show" class="advup-modal-fundo" @click.self="onClose">
        <div
          v-modal-acessivel="onClose"
          class="advup-modal advup-modal--claro advup-modal--teto fwd"
        >
          <div class="advup-modal__topo">
            <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
                 degradê escuro (05/10/2026). -->
            <i class="advup-modal__marca i-lucide-forward" aria-hidden="true" />
            <span class="advup-modal__selo" aria-hidden="true">
              <i class="i-lucide-forward" />
            </span>
            <div class="min-w-0 flex-1">
              <h2 class="advup-modal__titulo">
                {{ $t('CONVERSATION.FORWARD.TITLE') }}
              </h2>
              <p class="advup-modal__sub advup-modal__sub--solta">
                {{ $t('CONVERSATION.FORWARD.SUBTITLE') }}
              </p>
            </div>
            <button
              type="button"
              class="advup-modal__fechar"
              :aria-label="$t('CONVERSATION.FORWARD.BUTTON_CANCEL')"
              @click="onClose"
            >
              <i class="i-lucide-x" />
            </button>
          </div>

          <div class="advup-modal__corpo fwd-corpo">
            <div class="advup-busca advup-busca--bloco">
              <i class="advup-busca__lupa i-lucide-search" aria-hidden="true" />
              <input
                v-model="searchQuery"
                type="text"
                class="advup-busca__campo reset-base"
                :placeholder="$t('CONVERSATION.FORWARD.SEARCH_PLACEHOLDER')"
                :aria-label="$t('CONVERSATION.FORWARD.SEARCH_PLACEHOLDER')"
              />
              <Spinner v-if="buscandoNoServidor" size="small" />
            </div>

            <div class="fwd-lista">
              <!-- Carregando: a FORMA da lista que vem, não uma roda. -->
              <EsqueletoLista v-if="isLoading" :linhas="5" :altura="56" />

              <!-- Busca que falha não pode parecer "não achei nada": são coisas
                   diferentes e levam a pessoa a concluir que a conversa não
                   existe. -->
              <EstadoVazio
                v-else-if="erroDaBusca"
                tom="erro"
                icone="i-lucide-wifi-off"
                :titulo="$t('CONVERSATION.FORWARD.SEARCH_ERROR')"
              />

              <EsqueletoLista
                v-else-if="
                  filteredConversations.length === 0 && buscandoNoServidor
                "
                :linhas="3"
                :altura="56"
              />

              <EstadoVazio
                v-else-if="filteredConversations.length === 0"
                icone="i-lucide-message-circle-off"
                :titulo="$t('CONVERSATION.FORWARD.NO_CONVERSATIONS')"
              />

              <ul v-else class="fwd-itens">
                <li
                  v-for="(conversation, i) in filteredConversations"
                  :key="conversation.id"
                  class="advup-entra"
                  :style="{ '--i': Math.min(i, 10) }"
                >
                  <label class="fwd-linha">
                    <input
                      v-model="selectedConversationIds"
                      type="checkbox"
                      class="advup-caixa"
                      :value="conversation.id"
                    />

                    <Avatar
                      :src="conversation.meta?.sender?.thumbnail || ''"
                      :name="conversation.meta?.sender?.name || '?'"
                      :size="32"
                    />

                    <span class="fwd-linha__txt">
                      <span class="fwd-linha__topo">
                        <span class="fwd-linha__nome">
                          {{
                            conversation.meta?.sender?.name ||
                            $t('CONVERSATION.FORWARD.NO_NAME')
                          }}
                        </span>
                        <!-- A conversa em que a pessoa está agora fica marcada:
                             sem isso, encaminhar para ela mesma parece engano. -->
                        <span
                          v-if="conversation.id === currentConversationId?.id"
                          class="ficha-pill ficha-pill--ambar fwd-atual"
                        >
                          {{ $t('CONVERSATION.FORWARD.CURRENT_CONVERSATION') }}
                        </span>
                      </span>
                      <span class="fwd-linha__previa">
                        {{ getLastMessagePreview(conversation) }}
                      </span>
                    </span>

                    <span class="fwd-linha__meta">
                      <span
                        v-if="conversation.meta?.sender?.phone_number"
                        class="fwd-linha__fone"
                      >
                        {{ conversation.meta.sender.phone_number }}
                      </span>
                      <span class="fwd-linha__id">#{{ conversation.id }}</span>
                    </span>
                  </label>
                </li>
              </ul>
            </div>
          </div>

          <div class="advup-modal__rodape advup-modal__rodape--acoes">
            <!-- Quantas foram marcadas fica à esquerda, e conta subindo: é a
                 confirmação de que o clique na linha pegou. -->
            <span class="fwd-conta" aria-live="polite">
              <b class="fwd-conta__num">{{ selectedConversationIds.length }}</b>
              {{
                $t(
                  'CONVERSATION.FORWARD.FORWARD_TO_COUNT',
                  selectedConversationIds.length
                )
              }}
            </span>
            <span class="advup-modal__espaco" />
            <button
              type="button"
              class="advup-btn advup-btn--vazado"
              @click="onClose"
            >
              {{ $t('CONVERSATION.FORWARD.BUTTON_CANCEL') }}
            </button>
            <button
              type="button"
              class="advup-btn-forte"
              :disabled="selectedConversationIds.length === 0 || isForwarding"
              @click="handleForward"
            >
              <i
                :class="
                  isForwarding
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-forward'
                "
                aria-hidden="true"
              />
              {{ $t('CONVERSATION.FORWARD.BUTTON_FORWARD') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fwd {
  width: min(560px, calc(100vw - 2rem));
}

.fwd-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  min-height: 0;
}

.fwd-lista {
  overflow-y: auto;
  min-height: 16rem;
  max-height: 22rem;
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
}

.fwd-itens {
  margin: 0;
  padding: 0;
  list-style: none;
}

.fwd-itens > li + li {
  border-top: 1px solid rgb(var(--slate-3));
}

/* Azul = conversa. A faixa da esquerda cresce no hover e fica cheia quando
   marcada, igual ao cartão da lista de conversas — é o mesmo objeto. */
.fwd-linha {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-3);
  cursor: pointer;
  transition: background-color var(--mov-1) var(--mov-curva);
}

.fwd-linha::before {
  content: '';
  position: absolute;
  inset: var(--esp-2) auto var(--esp-2) 0;
  width: 3px;
  border-radius: 0 var(--r-1) var(--r-1) 0;
  background: rgb(var(--blue-9));
  scale: 1 0;
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}

.fwd-linha:hover {
  background: rgb(var(--slate-2));
}

.fwd-linha:hover::before {
  scale: 1 0.5;
}

.fwd-linha:has(.advup-caixa:checked) {
  background: rgb(var(--blue-2));
}

.fwd-linha:has(.advup-caixa:checked)::before {
  scale: 1 1;
}

.fwd-linha__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.fwd-linha__topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
}

.fwd-linha__nome {
  overflow: hidden;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fwd-atual {
  flex-shrink: 0;
}

.fwd-linha__previa {
  overflow: hidden;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fwd-linha__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-10));
  font-variant-numeric: tabular-nums;
}

.fwd-conta {
  display: inline-flex;
  align-items: baseline;
  gap: var(--esp-1);
  font-size: var(--txt-corpo);
  color: rgb(var(--slate-11));
}

.fwd-conta__num {
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .fwd-linha,
  .fwd-linha::before {
    transition: none;
  }
}

/* No celular o telefone sai da linha: o nome precisa do espaço. */
@media (max-width: 479px) {
  .fwd-linha__fone {
    display: none;
  }
}
</style>
